import { useCallback, useEffect, useState } from 'react';

const TOKEN_KEY = 'reporter.token';

export interface AuthUser {
  email: string;
  exp: number;
}

function decodeJwt(token: string): AuthUser | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64: replace chars and restore padding
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as unknown;
    if (
      typeof payload !== 'object' || payload === null ||
      !('sub' in payload) || typeof (payload as Record<string, unknown>).sub !== 'string' ||
      !('exp' in payload) || typeof (payload as Record<string, unknown>).exp !== 'number'
    ) return null;
    const p = payload as { sub: string; exp: number };
    return { email: p.sub, exp: p.exp };
  } catch {
    return null;
  }
}

function isExpired(user: AuthUser): boolean {
  return Date.now() / 1000 > user.exp;
}

function getStoredUser(): AuthUser | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    const user = decodeJwt(token);
    if (!user || isExpired(user)) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function authHeaders(): HeadersInit {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());

  const loginWithToken = useCallback((token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(decodeJwt(token));
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { detail?: string };
      throw new Error(data.detail ?? `Login failed: ${res.status}`);
    }
    const { access_token } = await res.json() as { access_token: string };
    localStorage.setItem(TOKEN_KEY, access_token);
    setUser(decodeJwt(access_token));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY) setUser(getStoredUser());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return { user, login, loginWithToken, logout, isAuthenticated: !!user };
}
